import React, {useState, useEffect} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import {useAuth0Data} from '../../../context/Auth0DataContext';

const {FiSettings, FiUser, FiBell, FiShield, FiSave, FiEdit} = FiIcons;

const ProfileSettings = () => {
  const {user} = useAuth0Data();
  const [isEditing, setIsEditing] = useState(false);
  const [profile, setProfile] = useState({
    name: '',
    email: '',
    role: 'User',
    phone: '',
    timezone: 'America/New_York',
    language: 'English'
  });
  const [notifications, setNotifications] = useState({
    emailReports: true,
    taskReminders: true,
    meetingAlerts: true,
    systemUpdates: false
  });
  const [security, setSecurity] = useState({
    twoFactorAuth: false,
    sessionTimeout: '30',
    passwordLastChanged: '2024-01-01'
  });

  // Load actual user data when component mounts or user changes
  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || user.email?.split('@')[0] || 'User',
        email: user.email || '',
        role: 'User', // This would come from your profiles table if needed
        phone: user.phone_number || '',
        timezone: user.zoneinfo || 'America/New_York',
        language: user.locale || 'English'
      });
    }
  }, [user]);

  const handleProfileSave = () => {
    setIsEditing(false);
    // In a real app, this would save to Supabase
    // You could update the user metadata here
  };

  const handleNotificationChange = (key) => {
    setNotifications(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleSecurityChange = (key, value) => {
    setSecurity(prev => ({
      ...prev,
      [key]: value
    }));
  };

  return (
    <motion.div
      initial={{opacity: 0, y: 20}}
      animate={{opacity: 1, y: 0}}
      className="space-y-6"
    >
      {/* Profile Information */}
      <motion.div
        initial={{opacity: 0, y: 10}}
        animate={{opacity: 1, y: 0}}
        className="bg-white rounded-xl shadow-soft border border-gray-100 p-6"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiUser} className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="ml-3 text-xl font-bold text-gray-900">Profile Information</h3>
          </div>
          <button
            onClick={() => isEditing ? handleProfileSave() : setIsEditing(true)}
            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <SafeIcon icon={isEditing ? FiSave : FiEdit} className="w-4 h-4 mr-2" />
            {isEditing ? 'Save' : 'Edit'}
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
            {isEditing ? (
              <input
                type="text"
                value={profile.name}
                onChange={(e) => setProfile(prev => ({...prev, name: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            ) : (
              <p className="text-gray-900">{profile.name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
            <p className="text-gray-900">{profile.email}</p>
            <p className="text-xs text-gray-500 mt-1">Email cannot be changed from profile settings</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
            <p className="text-gray-900">{profile.role}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
            {isEditing ? (
              <input
                type="tel"
                value={profile.phone}
                onChange={(e) => setProfile(prev => ({...prev, phone: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            ) : (
              <p className="text-gray-900">{profile.phone || 'Not set'}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
            {isEditing ? (
              <select
                value={profile.timezone}
                onChange={(e) => setProfile(prev => ({...prev, timezone: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="America/New_York">Eastern Time</option>
                <option value="America/Chicago">Central Time</option>
                <option value="America/Denver">Mountain Time</option>
                <option value="America/Los_Angeles">Pacific Time</option>
              </select>
            ) : (
              <p className="text-gray-900">{profile.timezone}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
            {isEditing ? (
              <select
                value={profile.language}
                onChange={(e) => setProfile(prev => ({...prev, language: e.target.value}))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="English">English</option>
                <option value="Spanish">Spanish</option>
                <option value="French">French</option>
              </select>
            ) : (
              <p className="text-gray-900">{profile.language}</p>
            )}
          </div>
        </div>
      </motion.div>

      {/* User ID and Account Info */}
      <motion.div
        initial={{opacity: 0, y: 10}}
        animate={{opacity: 1, y: 0}}
        transition={{delay: 0.05}}
        className="bg-white rounded-xl shadow-soft border border-gray-100 p-6"
      >
        <div className="flex items-center mb-4">
          <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
            <SafeIcon icon={FiShield} className="w-5 h-5 text-gray-600" />
          </div>
          <h3 className="ml-3 text-xl font-bold text-gray-900">Account Information</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">User ID</label>
            <p className="text-xs text-gray-600 font-mono bg-gray-50 p-2 rounded border">
              {user?.sub || 'Not available'}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Account Created</label>
            <p className="text-gray-900">
              {user?.updated_at ? new Date(user.updated_at).toLocaleDateString() : 'Not available'}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Last Updated</label>
            <p className="text-gray-900">
              {user?.updated_at ? new Date(user.updated_at).toLocaleDateString() : 'Not available'}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email Verified</label>
            <p className={`font-medium ${user?.email_verified ? 'text-green-600' : 'text-orange-600'}`}>
              {user?.email_verified ? 'Verified' : 'Pending verification'}
            </p>
          </div>
        </div>
      </motion.div>

      {/* Notification Settings */}
      <motion.div
        initial={{opacity: 0, y: 10}}
        animate={{opacity: 1, y: 0}}
        transition={{delay: 0.1}}
        className="bg-white rounded-xl shadow-soft border border-gray-100 p-6"
      >
        <div className="flex items-center mb-6">
          <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
            <SafeIcon icon={FiBell} className="w-5 h-5 text-yellow-600" />
          </div>
          <h3 className="ml-3 text-xl font-bold text-gray-900">Notification Preferences</h3>
        </div>

        <div className="space-y-4">
          {Object.entries(notifications).map(([key, value]) => (
            <div key={key} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <h4 className="font-medium text-gray-900">
                  {key === 'emailReports' && 'Email Reports'}
                  {key === 'taskReminders' && 'Task Reminders'}
                  {key === 'meetingAlerts' && 'Meeting Alerts'}
                  {key === 'systemUpdates' && 'System Updates'}
                </h4>
                <p className="text-sm text-gray-600">
                  {key === 'emailReports' && 'Receive weekly performance reports'}
                  {key === 'taskReminders' && 'Get notified about upcoming deadlines'}
                  {key === 'meetingAlerts' && 'Alerts for scheduled meetings'}
                  {key === 'systemUpdates' && 'Notifications about system maintenance'}
                </p>
              </div>
              <button
                onClick={() => handleNotificationChange(key)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  value ? 'bg-primary-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    value ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Security Settings */}
      <motion.div
        initial={{opacity: 0, y: 10}}
        animate={{opacity: 1, y: 0}}
        transition={{delay: 0.2}}
        className="bg-white rounded-xl shadow-soft border border-gray-100 p-6"
      >
        <div className="flex items-center mb-6">
          <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
            <SafeIcon icon={FiShield} className="w-5 h-5 text-red-600" />
          </div>
          <h3 className="ml-3 text-xl font-bold text-gray-900">Security Settings</h3>
        </div>

        <div className="space-y-6">
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <h4 className="font-medium text-gray-900">Two-Factor Authentication</h4>
              <p className="text-sm text-gray-600">Add an extra layer of security to your account</p>
            </div>
            <button
              onClick={() => handleSecurityChange('twoFactorAuth', !security.twoFactorAuth)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                security.twoFactorAuth ? 'bg-primary-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  security.twoFactorAuth ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="p-4 bg-gray-50 rounded-lg">
            <label className="block text-sm font-medium text-gray-700 mb-2">Session Timeout (minutes)</label>
            <select
              value={security.sessionTimeout}
              onChange={(e) => handleSecurityChange('sessionTimeout', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="15">15 minutes</option>
              <option value="30">30 minutes</option>
              <option value="60">1 hour</option>
              <option value="120">2 hours</option>
            </select>
          </div>

          <div className="p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">Password Management</h4>
            <p className="text-sm text-gray-600 mb-4">
              To change your password, use the User Management section if you're an admin, or contact an administrator.
            </p>
            <p className="text-sm text-gray-500">
              For security reasons, password changes require admin privileges or email verification.
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ProfileSettings;